import styles from './NewLink.module.css';
import {
	Button,
	Col,
	Divider,
	Form,
	Input,
	Modal,
	notification,
	Radio,
	RadioChangeEvent,
	Row,
	Space,
} from 'antd';
import { useEffect, useState } from 'react';

import { QRCodeCanvas } from 'qrcode.react';
import globirdLogo from 'assets/globirdLogo-black.svg';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faShareNodes } from '@fortawesome/free-solid-svg-icons';
import { generateReferralLink, sendReferralEmail } from 'services/RequestDataService';
import {
	DefaultDisplayDateFormat,
	GenerateReferralLinkRequest,
	ReferralLinkType,
	SendReferralRequest,
} from 'data/CustomerPortalTypes';
import { selectCurrentAccount, selectUser } from 'store/userSlice';
import { useSelector } from 'react-redux';
import moment from 'moment';
import ButtonWithToolTip from 'components/Shared/ButtonWithToolTip';
import { GlobirdOrPip } from 'components/Shared/GlobirdOr';

interface NewReferralProps {
	onLinkGenerated: () => void;
}

interface NewReferralLinkForm {
	name: string;
	email: string;
}

const NewReferral = (props: NewReferralProps) => {
	const { onLinkGenerated } = props;

	const [newReferralLinkForm] = Form.useForm<NewReferralLinkForm>();
	const [sendReferralEmailForm] = Form.useForm();

	const currentAccount = useSelector(selectCurrentAccount);
	const currentUser = useSelector(selectUser);

	const [referralLink, setReferralLink] = useState<string>();
	const [referralLinkId, setReferralLinkId] = useState<number>();
	const [referralExpiry, setReferralExpiry] = useState<string>('');
	const [copyButtonText, setCopyButtonText] = useState<string>('Copy Link');
	const [shareApiEnabled, setShareApiEnabled] = useState<boolean>(false);
	const [referralLinkType, setReferralLinkType] = useState<ReferralLinkType>(
		ReferralLinkType.Shared
	);
	const [loading, setLoading] = useState<boolean>(false);

	const [isModalOpen, setIsModalOpen] = useState(false);

	useEffect(() => {
		if (!navigator.canShare) {
			setShareApiEnabled(false);
		} else {
			setShareApiEnabled(true);
		}
	}, []);

	const showModal = () => {
		setIsModalOpen(true);
	};

	const closeModal = () => {
		setIsModalOpen(false);
		onLinkGenerated();
	};

	const onChange = (e: RadioChangeEvent) => {
		setReferralLinkType(e.target.value);
	};

	const copyToClipboard = async () => {
		if (!referralLink) {
			return;
		}

		await navigator.clipboard.writeText(referralLink);

		setCopyButtonText('Copied!');

		setTimeout(() => {
			setCopyButtonText('Copy Link');
		}, 4000);
	};

	const shareQRCode = async () => {
		const canvas = document.getElementById('canvas-qrcode') as HTMLCanvasElement;
		const dataUrl = canvas.toDataURL('image/png');
		const imageData = await fetch(dataUrl);
		const blob = await imageData.blob();
		const filesArray = [
			new File([blob], 'Referral.png', {
				type: blob.type,
			}),
		];

		const shareData = {
			files: filesArray,
		};

		await navigator.share(shareData);
	};

	const qrCodeElement = (
		<Space direction="vertical">
			<Space>
				<h5 className={styles['send-referral-scanqrcode-text']}>Scan or share QR code</h5>
				{shareApiEnabled ? (
					<FontAwesomeIcon
						color={'#e71d73'}
						icon={faShareNodes}
						size="lg"
						style={{ cursor: 'pointer' }}
						onClick={shareQRCode}
					/>
				) : null}
			</Space>
			<QRCodeCanvas
				id="canvas-qrcode"
				value={referralLink!}
				level="H"
				imageSettings={{
					src: globirdLogo,
					excavate: true,
					width: 55,
					height: 35,
				}}
				size={180}
				className={styles['referral-qr-code']}
			/>
		</Space>
	);

	const generateLink = async (values: NewReferralLinkForm) => {
		if (!currentAccount || !currentUser) {
			return;
		}

		var label = referralLinkType === ReferralLinkType.Personalised ? values.name : undefined;
		const generateReferralLinkRequest: GenerateReferralLinkRequest = {
			label: label,
			senderEmail: currentUser.emailAddress,
			accountIdOrAccountNumber: currentAccount.accountId.toString()
		};
		const result = await generateReferralLink(
			currentAccount.accountId,
			referralLinkType,
			generateReferralLinkRequest
		);

		if (!result?.success || !result.data) {
			return;
		}

		setReferralLink(result.data.url);
		setReferralLinkId(result.data.referralLinkId);

		const date = moment(result.data.expiryDate);
		if (date.isValid()) {
			setReferralExpiry(moment(date).format(DefaultDisplayDateFormat));
		}

		showModal();
	};

	const sendReferral = async (values: any) => {
		if (!currentAccount || !referralLinkId || !currentUser) {
			return;
		}

		setLoading(true);

		const email = newReferralLinkForm.getFieldValue('email');
		const name = newReferralLinkForm.getFieldValue('name');

		const sendReferralRequest: SendReferralRequest = {
			referralLinkId: referralLinkId,
			recipientEmail: email,
			recipientName: name,
			message: values.message,
			senderEmail: currentUser.emailAddress,
			accountIdOrAccountNumber: currentAccount.accountId.toString()
		};
		const result = await sendReferralEmail(currentAccount.accountId, sendReferralRequest);

		if (!result?.success) {
			setLoading(false);
			return;
		}

		newReferralLinkForm.resetFields();
		sendReferralEmailForm.resetFields();

		setLoading(false);
		closeModal();

		notification.success({
			message: 'Success',
			description: 'Referral link sent successfully',
		});
	};

	return (
		<div className={styles['send-referral-block']}>
			<Form
				form={newReferralLinkForm}
				name="send-referral"
				layout={'vertical'}
				onFinish={generateLink}
			>
				<Row>
					<Col xs={8} sm={12} md={12} lg={6}>
						<Form.Item>
							<Radio.Group onChange={onChange} value={referralLinkType}>
								<Radio value={ReferralLinkType.Shared}>Shared</Radio>
								<Radio value={ReferralLinkType.Personalised}>Personalised</Radio>
							</Radio.Group>
						</Form.Item>
					</Col>
					{referralLinkType === ReferralLinkType.Shared ? (
						<h5 className={styles['send-referral-subheading']}>
							Click the button below to generate a shared referral link
						</h5>
					) : null}
					{referralLinkType === ReferralLinkType.Personalised ? (
						<>
							<Col xs={24} sm={12} md={12} lg={16}>
								<h5 className={styles['send-referral-subheading']}>
									Enter your friend's details to generate a personalised referral link
								</h5>
								<Form.Item
									label="Name"
									name="name"
									rules={[
										{
											required: true,
											message: 'Name is required',
										},
									]}
								>
									<Input maxLength={50} />
								</Form.Item>
								<Form.Item
									label="Email"
									name="email"
									rules={[
										{
											required: true,
											message: 'Email address is required',
										},
										{ type: 'email', message: 'Email is invalid' },
									]}
									normalize={(value) => value?.replace(/\s/g, '')}
								>
									<Input maxLength={200} />
								</Form.Item>
							</Col>
						</>
					) : null}
				</Row>
				<Row justify="center">
					<Form.Item>
						<Button type="primary" size="large" htmlType="submit">
							Generate
						</Button>
					</Form.Item>
				</Row>
			</Form>
			<Modal width={650} visible={isModalOpen} footer={null} onCancel={closeModal}>
				<h5 className={styles['send-referral-subheading']}>Copy your referral link</h5>
				<Input.Group style={{ display: 'flex', marginBottom: '10px' }}>
					<Input
						readOnly
						style={{ flex: '1 1 auto' }}
						value={referralLink}
						id="referral-link"
						title="referral link"
					/>
					<ButtonWithToolTip
						disabled={false}
						btnNode={<span>{copyButtonText}</span>}
						useAntdBtn
						componentClassName={[styles['copy-button-wrapper']]}
						btnClassName={[styles['copy-button']]}
						onClick={copyToClipboard}
					/>
				</Input.Group>
				{referralExpiry ? (
					<p>
						<u>Note:</u> the referral link above will expire on {referralExpiry}.
					</p>
				) : null}

				<GlobirdOrPip />
				<Row justify="center">{qrCodeElement}</Row>
				<Divider />
				{referralLinkType === ReferralLinkType.Personalised ? (
					<>
						Include an optional message below, and we will send an email to{' '}
						<span style={{ color: '#ec008c' }}>{newReferralLinkForm.getFieldValue('email')}</span>{' '}
						with a personalised referral link.
						<Form
							form={sendReferralEmailForm}
							style={{ marginTop: '10px' }}
							onFinish={sendReferral}
						>
							<Form.Item name="message" wrapperCol={{ span: 24 }}>
								<Input.TextArea placeholder="Message" maxLength={200} />
							</Form.Item>
							<Form.Item>
								<Row justify="center">
									<Button type="primary" size="large" htmlType="submit" loading={loading}>
										Send Referral
									</Button>
								</Row>
							</Form.Item>
						</Form>
					</>
				) : null}
			</Modal>
		</div>
	);
};

export default NewReferral;
